/**
 * transform 字符串 ⇄ 矩阵。ops 写回时的通用形态是「前置叠加」：
 *   el.setAttribute('transform', `${toString(newMat)} ${oldStr}`)
 * 矩阵乘法顺序天然正确，所以无需归并归一化（也就不会无谓改动文件）。
 */
import { IDENTITY, around, mul, rotate, scale, toString, translate, type Mat } from './matrix';

const rad = (deg: number): number => (deg * Math.PI) / 180;

const nums = (s: string): number[] =>
  s
    .split(/[\s,]+/)
    .filter(Boolean)
    .map((n) => Number(n));

export interface TransformParse {
  mat: Mat;
  /** 出现无法识别的数值（NaN）时为 false —— 调用方应放弃改写而非写入脏值 */
  ok: boolean;
}

const skewX = (deg: number): Mat => [1, 0, Math.tan(rad(deg)), 1, 0, 0];
const skewY = (deg: number): Mat => [1, Math.tan(rad(deg)), 0, 1, 0, 0];

/** 解析 SVG transform 列表 → 单个矩阵（按书写顺序左乘复合） */
export function parseTransformList(value: string | null | undefined): TransformParse {
  if (!value) return { mat: IDENTITY, ok: true };
  let mat: Mat = IDENTITY;
  let ok = true;
  for (const hit of value.matchAll(/(matrix|translate|scale|rotate|skewX|skewY)\s*\(([^)]*)\)/g)) {
    const fn = hit[1] as string;
    const v = nums(hit[2] as string);
    if (v.length === 0 || v.some((n) => !Number.isFinite(n))) {
      ok = false;
      continue;
    }
    switch (fn) {
      case 'matrix':
        mat = mul(mat, v.length === 6 ? (v as unknown as Mat) : IDENTITY);
        break;
      case 'translate':
        mat = mul(mat, translate(v[0] as number, v[1] ?? 0));
        break;
      case 'scale':
        mat = mul(mat, scale(v[0] as number, v[1] ?? v[0]));
        break;
      case 'rotate':
        mat =
          v.length >= 3
            ? mul(mat, around(rotate(v[0] as number), v[1] as number, v[2] as number))
            : mul(mat, rotate(v[0] as number));
        break;
      case 'skewX':
        mat = mul(mat, skewX(v[0] as number));
        break;
      case 'skewY':
        mat = mul(mat, skewY(v[0] as number));
        break;
    }
  }
  return { mat, ok };
}

/** 从矩阵反推旋转角（度），归一到 (-180, 180]；含斜切时给出的是等效旋转 */
export function rotationOf(m: Mat): number {
  const deg = (Math.atan2(m[1], m[0]) * 180) / Math.PI;
  if (!Number.isFinite(deg)) return 0;
  const norm = ((deg % 360) + 360) % 360;
  return norm > 180 ? norm - 360 : norm;
}

export const rotationOfElement = (el: Element): number =>
  rotationOf(parseTransformList(el.getAttribute('transform')).mat);

/** 叠加世界变换：新矩阵前置拼到已有 transform 之前 */
export function prependTransform(world: Mat, existing: string | null): string {
  const head = toString(world);
  return existing && existing.trim() !== '' ? `${head} ${existing}` : head;
}
